<template>
    <v-form ref="form" v-model="isValid" class="attribute-filter">
        <vuex-select
            class="attribute-filter-values"
            item-value="attributeKey"
            item-text="attributeName"
            autocomplete
            value-type="object"
            :getter="getAttribute"
            :setter="value => setAttribute(value)"
            :options="attributeOptions"
            :validations="attributeSelectValidations"
            prioritize-exact-match
        />
        <div v-if="showValuesSelect || showRangeModeSelector" class="attribute-filter-values-group">
            <vuex-select
                v-if="showValuesSelect"
                :class="[
                    'attribute-filter-values',
                    { 'attribute-filter-values--exclude-mode': isExcludeMode },
                ]"
                autocomplete
                chips
                multiple
                :disabled="!atributeValuesOptions.length"
                :getter="() => getAttributeValues()"
                :setter="setAttributeValues"
                :options="valueOptionsWithModeCue"
                :validations="valueSelectValidations"
                :sort-function="naturalSortOptions"
                prioritize-exact-match
            />

            <div v-if="showRangeInputs" class="attribute-filter-range-inputs">
                <vuex-number-input
                    v-if="showMinInput"
                    class="attribute-filter-range-inputs__field"
                    :getter="getMinValue"
                    :setter="setMinValue"
                    format="numbers.default.numberWithDecimals"
                    :validations="minValueValidations"
                    :disabled="isReadOnly"
                    :placeholder="$tkey('minValueLabel')"
                    :hide-details="'auto'"
                    @change="onRangeValueChange"
                />
                <vuex-number-input
                    v-if="showMaxInput"
                    class="attribute-filter-range-inputs__field"
                    :getter="getMaxValue"
                    :setter="setMaxValue"
                    format="numbers.default.numberWithDecimals"
                    :validations="maxValueValidations"
                    :disabled="isReadOnly"
                    :placeholder="$tkey('maxValueLabel')"
                    :hide-details="'auto'"
                    @change="onRangeValueChange"
                />
            </div>
            <vuex-select
                v-if="showRangeModeSelector"
                class="attribute-filter-range-mode"
                :getter="getRangeOperator"
                :setter="setRangeOperator"
                :options="rangeModeOptions"
                :disabled="isRangeModeSelectorDisabled"
                item-text="label"
                item-value="value"
                :sort-results="false"
            />
            <div v-if="showValueModeSelector" class="attribute-filter-value-mode-row">
                <vuex-select
                    class="attribute-filter-value-mode"
                    :getter="getValueMode"
                    :setter="setValueMode"
                    :options="attributeValueModeOptions"
                    :disabled="isReadOnly"
                    item-text="label"
                    item-value="value"
                    :sort-results="false"
                />
                <v-tooltip location="top" :max-width="300">
                    <template #activator="{ props }">
                        <v-icon
                            v-bind="props"
                            class="attribute-filter-value-mode-icon"
                            :color="isExcludeMode ? 'error' : 'primary'"
                            :aria-label="valueModeIndicatorTooltip"
                        >
                            {{ valueModeIndicatorIcon }}
                        </v-icon>
                    </template>
                    <span>{{ valueModeIndicatorTooltip }}</span>
                </v-tooltip>
            </div>
        </div>
        <div class="pt-1">
            <spinner-dynamic v-if="isLoading" />
            <v-btn
                v-else
                :disabled="isReadOnly || removeBtnDisabled"
                icon
                class="attribute-filter__remove-attribute"
                density="compact"
                elevation="0"
                variant="flat"
                @click="onRemoveAttributeRow"
            >
                <icon icon-name="custom:cancel" small icon-colour-type="primary-path" />
            </v-btn>
        </div>
    </v-form>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { get, set, isNil, isNumber, isString, toNumber, isFinite } from 'lodash';
import validators from '@/js/validators';
import attributeFilterValueSelectionModes from '@enums/attribute-filter-value-selection-modes';
import {
    filteredAttribureOptions as filteredAttribureOptionsUtil,
    setAttributeValues as setAttributeValuesUtil,
    setAttribute as setAttributeUtil,
    naturalSortOptions as naturalSortOptionsUtil,
    applyAvailableValuesWatcher,
} from '@/js/pages/planning/utils/attribute-filter-utils';

const RANGE_OPERATORS = ['gte', 'between', 'lte'];
const VALUE_SELECTION_MODES = Object.values(attributeFilterValueSelectionModes);

const { INCLUDE, EXCLUDE } = attributeFilterValueSelectionModes;

const normalizeNumericValue = value => value.replace(',', '.');

const isFiniteNumericValue = value => {
    if (isNumber(value)) {
        return isFinite(value);
    }

    if (isString(value)) {
        const trimmedValue = value.trim();

        if (!trimmedValue) {
            return false;
        }

        return isFinite(toNumber(normalizeNumericValue(trimmedValue)));
    }

    return false;
};

const toNullableNumber = value => {
    if (isNil(value) || value === '') {
        return null;
    }

    const parsedValue = toNumber(normalizeNumericValue(String(value)));

    return isFinite(parsedValue) ? parsedValue : value;
};

export default {
    localizationKey: 'planning.promotionsViewer.attributeScopePromotion.attributeFilterRow',
    props: {
        attributeRowIndex: {
            type: Number,
            required: true,
        },
        attributeScope: {
            type: Array,
            required: true,
        },
        validateAttributeSelect: {
            type: Boolean,
            default: true,
        },
        availableAttributeKeys: {
            type: Array,
            default: () => [],
        },
        availableValues: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['products-number-updated', 'remove-attribute-row'],
    data() {
        return {
            atributeValuesOptions: [],
            excludeModeOptionsCacheBySource: new WeakMap(),
            isValid: true,
            isLoading: false,
            validations: [
                {
                    validator: validators.required,
                },
            ],
        };
    },
    computed: {
        ...mapState('clientConfig', ['toggleLogic']),
        ...mapGetters('attributeMetadata', [
            'getAvailableAttributes',
            'getAvailableAttributesKeyedByAttributeKey',
            'getPlanningFilterAttributes',
        ]),
        attributeRow() {
            return this.attributeScope[this.attributeRowIndex];
        },
        rowAttrubuteKey() {
            return this.attributeRow.attributeKey || null;
        },
        filteredAttribureOptions() {
            // ...this spread drops props on a Vue 3 Proxy; pass each property explicitly
            return filteredAttribureOptionsUtil({
                availableAttributeKeys: this.availableAttributeKeys,
                rowAttrubuteKey: this.rowAttrubuteKey,
                attributeRow: this.attributeRow,
                attributeScope: this.attributeScope,
                getAvailableAttributes: this.getPlanningFilterAttributes,
            });
        },
        attributeOptions() {
            return this.filteredAttribureOptions.map(attribute => ({
                attributeKey: attribute.attributeKey,
                attributeName: attribute.attributeName,
            }));
        },
        attributeSelectValidations() {
            if (this.isReadOnly || !this.validateAttributeSelect) {
                return [];
            }
            return this.validations;
        },
        valueSelectValidations() {
            if (this.isReadOnly || !this.showValuesSelect) {
                return [];
            }

            return this.validations;
        },
        isRangeModeFeatureEnabled() {
            return get(this.toggleLogic, 'enableAttributeRangeMode', false);
        },
        isValueExclusionFeatureEnabled() {
            return get(this.toggleLogic, 'enableAttributeValueExclusionFiltering', false);
        },
        isRangeCapableAttribute() {
            return this.attributeRow.isRangeCapable === true;
        },
        showRangeModeSelector() {
            return this.isRangeModeFeatureEnabled && this.isRangeCapableAttribute;
        },
        isRangeModeSelectorDisabled() {
            return this.isReadOnly;
        },
        rangeModeOptions() {
            return [
                {
                    value: null,
                    label: this.$tkey('rangeOperatorNone'),
                },
                {
                    value: 'gte',
                    label: this.$tkey('rangeOperatorGte'),
                },
                {
                    value: 'between',
                    label: this.$tkey('rangeOperatorBetween'),
                },
                {
                    value: 'lte',
                    label: this.$tkey('rangeOperatorLte'),
                },
            ];
        },
        showRangeInputs() {
            return (
                this.showRangeModeSelector &&
                RANGE_OPERATORS.includes(this.attributeRow.rangeOperator)
            );
        },
        showMinInput() {
            return (
                this.attributeRow.rangeOperator === 'gte' ||
                this.attributeRow.rangeOperator === 'between'
            );
        },
        showMaxInput() {
            return (
                this.attributeRow.rangeOperator === 'lte' ||
                this.attributeRow.rangeOperator === 'between'
            );
        },
        showValuesSelect() {
            if (!this.showRangeModeSelector) {
                return true;
            }

            return !RANGE_OPERATORS.includes(this.attributeRow.rangeOperator);
        },
        showValueModeSelector() {
            return this.isValueExclusionFeatureEnabled;
        },
        isExcludeMode() {
            return this.showValueModeSelector && this.getValueMode() === EXCLUDE;
        },
        attributeValueModeOptions() {
            return [
                {
                    value: INCLUDE,
                    label: this.$tkey('valueModeInclude'),
                },
                {
                    value: EXCLUDE,
                    label: this.$tkey('valueModeExclude'),
                },
            ];
        },
        valueModeIndicatorIcon() {
            return 'mdi-information-outline';
        },
        valueModeIndicatorTooltip() {
            return this.isExcludeMode
                ? this.$tkey('valueModeIndicatorTooltipExclude')
                : this.$tkey('valueModeIndicatorTooltipInclude');
        },
        valueOptionsWithModeCue() {
            if (!this.isExcludeMode) {
                return this.atributeValuesOptions;
            }

            const excludedValueMarker = this.$tkey('excludedValueMarker');
            const cachedEntry = this.excludeModeOptionsCacheBySource.get(
                this.atributeValuesOptions
            );

            if (cachedEntry?.excludedValueMarker === excludedValueMarker) {
                return cachedEntry.options;
            }

            const transformedOptions = this.atributeValuesOptions.map(option => {
                if (typeof option === 'object' && option !== null) {
                    const baseText = option.text || String(option.value);
                    return {
                        ...option,
                        text: `${baseText} (${excludedValueMarker})`,
                    };
                }

                return {
                    text: `${String(option)} (${excludedValueMarker})`,
                    value: option,
                };
            });

            this.excludeModeOptionsCacheBySource.set(this.atributeValuesOptions, {
                excludedValueMarker,
                options: transformedOptions,
            });

            return transformedOptions;
        },
        minValueValidations() {
            if (this.isReadOnly || !this.showMinInput) {
                return [];
            }

            return [
                {
                    validator: value => !isNil(toNullableNumber(value)),
                    message: this.$tkey('validationMinRequired'),
                },
                {
                    validator: value => isFiniteNumericValue(value),
                    message: this.$tkey('validationNumericValue'),
                },
            ];
        },
        maxValueValidations() {
            if (this.isReadOnly || !this.showMaxInput) {
                return [];
            }

            return [
                {
                    validator: value => !isNil(toNullableNumber(value)),
                    message: this.$tkey('validationMaxRequired'),
                },
                {
                    validator: value => isFiniteNumericValue(value),
                    message: this.$tkey('validationNumericValue'),
                },
                {
                    validator: value => {
                        if (this.attributeRow.rangeOperator !== 'between') {
                            return true;
                        }

                        const minValue = toNullableNumber(this.attributeRow.minValue);
                        const maxValue = toNullableNumber(value);

                        if (isNil(minValue) || isNil(maxValue)) {
                            return true;
                        }

                        return minValue <= maxValue;
                    },
                    message: this.$tkey('validationRangeOrder'),
                },
            ];
        },
        removeBtnDisabled() {
            return (
                !this.validateAttributeSelect &&
                !this.attributeRowIndex &&
                !this.attributeRow.attributeKey
            );
        },
    },
    watch: {
        availableValues: {
            handler(newValues) {
                applyAvailableValuesWatcher(this, newValues);
            },
            immediate: true,
        },
    },
    mounted() {
        if (this.$refs.form && !this.isReadOnly) {
            this.$refs.form.validate();
        }
    },
    methods: {
        naturalSortOptions(options) {
            return naturalSortOptionsUtil(options);
        },
        ensureValidMode(row) {
            if (!VALUE_SELECTION_MODES.includes(row.mode)) {
                set(row, 'mode', INCLUDE);
            }
        },
        getAttribute() {
            if (!this.attributeRow.attributeKey) {
                return null;
            }

            return {
                attributeKey: this.attributeRow.attributeKey,
                attributeName: this.attributeRow.attributeName,
            };
        },
        getAttributeValues() {
            return this.attributeRow.values;
        },
        getRangeOperator() {
            return this.attributeRow.rangeOperator;
        },
        setRangeOperator(value) {
            this.onRangeOperatorSelected(value);
        },
        getValueMode() {
            return VALUE_SELECTION_MODES.includes(this.attributeRow.mode)
                ? this.attributeRow.mode
                : INCLUDE;
        },
        setValueMode(value) {
            this.onValueModeSelected(value);
        },
        getMinValue() {
            return this.attributeRow.minValue;
        },
        getMaxValue() {
            return this.attributeRow.maxValue;
        },
        setMinValue(value) {
            this.onRangeValueUpdated('minValue', value);
        },
        setMaxValue(value) {
            this.onRangeValueUpdated('maxValue', value);
        },
        setAttributeValues(values) {
            setAttributeValuesUtil(this, values);
        },
        async setAttribute(value) {
            await setAttributeUtil(this, value);

            const currentRow = this.attributeScope[this.attributeRowIndex];

            set(currentRow, 'rangeOperator', null);
            set(currentRow, 'minValue', null);
            set(currentRow, 'maxValue', null);
            this.ensureValidMode(currentRow);
        },
        onRangeOperatorSelected(rangeOperator) {
            if (this.isReadOnly) {
                return;
            }

            const currentRow = this.attributeScope[this.attributeRowIndex];
            const normalizedOperator = RANGE_OPERATORS.includes(rangeOperator)
                ? rangeOperator
                : null;

            set(currentRow, 'rangeOperator', normalizedOperator);

            if (!normalizedOperator) {
                set(currentRow, 'minValue', null);
                set(currentRow, 'maxValue', null);
                this.ensureValidMode(currentRow);
            }

            if (normalizedOperator === 'gte') {
                set(currentRow, 'maxValue', null);
            }

            if (normalizedOperator === 'lte') {
                set(currentRow, 'minValue', null);
            }

            if (normalizedOperator) {
                set(currentRow, 'values', []);
                this.ensureValidMode(currentRow);
            }

            if (this.$refs.form) {
                this.$refs.form.validate();
            }

            if (!normalizedOperator) {
                this.$emit('products-number-updated');
            }
        },
        onValueModeSelected(mode) {
            if (this.isReadOnly || !this.showValueModeSelector) {
                return;
            }

            const currentRow = this.attributeScope[this.attributeRowIndex];
            const normalizedMode = VALUE_SELECTION_MODES.includes(mode) ? mode : INCLUDE;

            set(currentRow, 'mode', normalizedMode);
            this.$emit('products-number-updated');
        },
        async onRangeValueUpdated(fieldName, value) {
            if (this.isReadOnly) {
                return;
            }

            const currentRow = this.attributeScope[this.attributeRowIndex];
            set(currentRow, fieldName, toNullableNumber(value));

            if (this.$refs.form) {
                const { valid } = await this.$refs.form.validate();
                if (!valid) {
                    return;
                }
            }

            this.$emit('products-number-updated');
        },
        onRangeValueChange() {
            if (this.$refs.form) {
                this.$refs.form.validate();
            }
        },
        onRemoveAttributeRow() {
            this.atributeValuesOptions = [];
            this.$emit('remove-attribute-row', {
                index: this.attributeRowIndex,
                key: this.attributeRow.key,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.attribute-filter {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;

    .rtls-select-container {
        width: 30%;
    }

    .attribute-filter-values-group {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        width: 30%;

        .rtls-select-container {
            width: 100%;

            :deep(.v-input--density-default .v-field__input) {
                flex-wrap: wrap;
            }
        }
    }

    .attribute-filter-range-mode {
        width: 100%;
    }

    .attribute-filter-value-mode-row {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        width: 100%;
        position: relative;
    }

    .attribute-filter-value-mode {
        width: 100%;
    }

    .attribute-filter-value-mode-icon {
        flex-shrink: 0;
        font-size: 2rem;
        position: absolute;
        right: -3.2rem;
    }

    .attribute-filter-range-inputs {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
        gap: 1rem;

        &__field {
            :deep() {
                .v-input__details {
                    min-height: 1.2rem;
                }

                input {
                    text-align: left;
                }
            }
        }
    }

    .attribute-filter-values {
        :deep() {
            .v-input__control {
                min-height: 3.2rem;
            }

            .v-field__input {
                padding: 0.2rem;
                flex-wrap: wrap;
            }

            .v-select__slot {
                height: auto !important;
                min-height: 2.2rem;
                min-width: 25rem;
            }

            .v-input--is-disabled {
                .v-text-field__details {
                    display: none;
                }
            }
        }
    }

    .attribute-filter-values--exclude-mode {
        :deep(.v-field) {
            border: 0.1rem solid $vuetify-error-red;
        }
    }

    &__remove-attribute.v-btn--disabled {
        color: rgba(var(--v-theme-on-surface), 0.26) !important;
    }
}
</style>
